import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

export type ButtonItem = {
  id: string;
  label?: string;
  icon: React.ElementType;
  isPlaceholder?: boolean;
  show?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
  hasDropdown?: boolean;
};

export const DropdownMenu: React.FC<{
  visible: boolean;
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
}> = ({ visible, children, className = '', onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      className={`absolute top-full left-0 mt-1 bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-lg py-1.5 min-w-[180px] z-[9999] ${className}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
};

export const DropdownItem: React.FC<{
  icon?: React.ElementType;
  label: string;
  onClick?: () => void;
  hasSubmenu?: boolean;
  children?: React.ReactNode;
}> = ({ icon: Icon, label, onClick, hasSubmenu, children }) => {
  const [subOpen, setSubOpen] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => hasSubmenu && setSubOpen(true)}
      onMouseLeave={() => hasSubmenu && setSubOpen(false)}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-white hover:text-white hover:bg-white/10 transition-colors text-left"
      >
        {Icon && <Icon size={16} className="text-white/60" />}
        <span className="flex-1">{label}</span>
        {hasSubmenu && <ChevronDown size={14} className="text-white/40 -rotate-90" />}
      </button>
      {hasSubmenu && subOpen && children}
    </div>
  );
};

export const GridSplitDropdown: React.FC<{
  visible: boolean;
  onSelect: (rows: number, cols: number) => void;
  onClose: () => void;
}> = ({ visible, onSelect, onClose }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const MAX_ROWS = 5;
  const MAX_COLS = 5;

  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  if (!visible) return null;

  const presets = [
    { label: '4宫格 (2×2)', rows: 2, cols: 2 },
    { label: '9宫格 (3×3)', rows: 3, cols: 3 },
    { label: '16宫格 (4×4)', rows: 4, cols: 4 },
    { label: '25宫格 (5×5)', rows: 5, cols: 5 },
  ];

  const handlePreset = (rows: number, cols: number) => {
    onSelect(rows, cols);
    onClose();
  };

  const handleGridHover = (row: number, col: number) => {
    setHoverRows(row);
    setHoverCols(col);
  };

  const handleGridClick = () => {
    if (hoverRows > 0 && hoverCols > 0) {
      onSelect(hoverRows, hoverCols);
      onClose();
    }
  };

  return (
    <div
      ref={menuRef}
      className={`absolute top-full left-0 mt-1 z-[9999] flex ${visible ? '' : 'hidden'}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-lg py-1.5 min-w-[160px]">
        {presets.map((p) => (
          <button
            key={p.label}
            onClick={() => handlePreset(p.rows, p.cols)}
            className="w-full text-left px-3 py-2 text-sm text-white hover:text-white hover:bg-white/10 transition-colors"
          >
            {p.label}
          </button>
        ))}
        <div className="mx-3 my-1 h-px bg-white/10" />
        <div
          className="relative"
          onMouseEnter={() => setShowCustom(true)}
          onMouseLeave={() => setShowCustom(false)}
        >
          <button className="w-full flex items-center justify-between px-3 py-2 text-sm text-white hover:text-white hover:bg-white/10 transition-colors">
            <span>自定义</span>
            <ChevronDown size={14} className="text-white/40 -rotate-90" />
          </button>
        </div>
      </div>

      {showCustom && (
        <div
          className="ml-2 bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-lg p-4 min-w-[200px]"
          onMouseEnter={() => setShowCustom(true)}
          onMouseLeave={() => setShowCustom(false)}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-white">自定义宫格</div>
            <div className="text-sm text-white/60">
              {hoverRows > 0 && hoverCols > 0 ? `${hoverCols} x ${hoverRows}` : ''}
            </div>
          </div>
          <div
            className="grid gap-1.5 mx-auto"
            style={{
              gridTemplateColumns: `repeat(${MAX_COLS}, 1fr)`,
              width: 'fit-content',
            }}
          >
            {Array.from({ length: MAX_ROWS * MAX_COLS }).map((_, i) => {
              const row = Math.floor(i / MAX_COLS) + 1;
              const col = (i % MAX_COLS) + 1;
              const isActive = row <= hoverRows && col <= hoverCols;
              return (
                <div
                  key={i}
                  className={`w-7 h-7 rounded-sm cursor-pointer transition-colors ${
                    isActive ? 'bg-blue-500/80' : 'bg-white/10 hover:bg-white/20'
                  }`}
                  onMouseEnter={() => handleGridHover(row, col)}
                  onClick={handleGridClick}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
